import BaseTibbit from '@appblocks/codegeneration/Tibbits/_BaseTibbit';
import Tibbits from '@appblocks/codegeneration/Tibbits/index';
import {
    CodeBlock,
    BlockType,
    SettingType,
    ValidationMessage,
    ValidationMessageType,
    Setting,
    SettingState,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export default class Tibbit_33 extends BaseTibbit {
    constructor() {
        super();
        this.id = 'tibbit_33';
        this.name = 'Tibbit 33';
        const settings: Setting[] = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
                required: true,
            },
            {
                id: 'PF',
                name: 'Power Fail Monitoring',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
        ];
        this.settings = this.settings.concat(settings);
    }

    getState(settingId: string): SettingState {
        if (settingId === 'PF' && Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id)?.interrupt === undefined) {
            return SettingState.DISABLED;
        }
        return SettingState.ENABLED;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return [];
        }

        const interruptNumber = Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id)?.interrupt;
        const interruptEnum = PlatformUtils.getEnum(interruptNumber, 'pl_int_num', this.project.device.platform);
        const interruptValue = PlatformUtils.getEnumValue(interruptNumber, 'pl_int_num', this.project.device.platform);
        const interruptBit = Math.pow(2, Number(interruptValue));
        let interruptBlock = '';

        if (this.getSettingValue('PF') === '1') {
            blocks.push({
                location: 'GLOBAL_DIMS',
                blockType: BlockType.CODE,
                id: `S${this.getSettingValue('SOCKET')}_power_fail_dim`,
                template: `dim S${this.getSettingValue('SOCKET')}_power_fail_counter as byte = 0`,
            });

            blocks.push({
                location: 'GLOBAL_DECLARATIONS',
                blockType: BlockType.CODE,
                id: `S${this.getSettingValue('SOCKET')}_power_fail_declare`,
                template: `declare S${this.getSettingValue('SOCKET')}_power_fail_counter as byte`,
            });

            blocks.push({
                location: 'on_io_int',
                blockType: BlockType.CODE,
                template: `
    if (linestate and ${interruptBit}) <>0 then
        dim line_state as low_high=io.lineget(TBT33_PGOOD_S${this.getSettingValue('SOCKET')})
        if line_state=LOW then
            S${this.getSettingValue('SOCKET')}_power_fail_counter=S${this.getSettingValue('SOCKET')}_power_fail_counter+1
            sys.debugprint(str(S${this.getSettingValue('SOCKET')}_power_fail_counter) + " power failures detected on the Tibbit #33 in socket ${this.getSettingValue('SOCKET')}\\r\\n")
            'After a certain number of failures, you can initiate fail safe operations.
        end if   
    end if`,
            });
        }

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            id: 'power_fail_declare',
            template: `declare sub tbt33_system_hard_reboot()
declare sub tbt_33_shut_down(shutdown_io as pl_io_num)
declare sub tbt_33_enable(shutdown_io as pl_io_num) `,
        });

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const TBT33_SHUTDOWN_S${this.getSettingValue('SOCKET')} = ${Tibbits.getSocketPins(this.getSettingValue('SOCKET'), this.project.device.id)[0]}
const TBT33_NC1_S${this.getSettingValue('SOCKET')} = ${Tibbits.getSocketPins(this.getSettingValue('SOCKET'), this.project.device.id)[1]}
const TBT33_NC2_S${this.getSettingValue('SOCKET')} = ${Tibbits.getSocketPins(this.getSettingValue('SOCKET'), this.project.device.id)[2]}
const TBT33_PGOOD_S${this.getSettingValue('SOCKET')} = ${Tibbits.getSocketPins(this.getSettingValue('SOCKET'), this.project.device.id)[3]}`,
        });

        if (interruptEnum) {
            interruptBlock = `
    io.num=TBT33_PGOOD_S${this.getSettingValue('SOCKET')}
    io.enabled=NO
    io.state=HIGH
    io.intnum=${interruptEnum}
    io.intenabled=YES`;
        }

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    io.num=TBT33_SHUTDOWN_S${this.getSettingValue('SOCKET')}
    io.enabled=YES
    io.state=HIGH
    
    io.num=TBT33_NC1_S${this.getSettingValue('SOCKET')}
    io.enabled=NO
    io.state=HIGH
    
    io.num=TBT33_NC2_S${this.getSettingValue('SOCKET')}
    io.enabled=NO
    io.state=HIGH

    ${interruptBlock}    
    `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.LOCATION,
            id: 'tbt33_system_hard_reboot',
            template: `
sub tbt33_system_hard_reboot()
'Pulling the shutdown line low will trigger a system hard reboot.  
'Special care should be taken when doing this.
    '****tbt33_system_hard_reboot****'
end sub    
        `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.LOCATION,
            id: 'tbt_33_shut_down',
            template: `
sub tbt_33_shut_down(shutdown_io as pl_io_num)
    io.num=shutdown_io 
    io.state=LOW
end sub
        `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.LOCATION,
            id: 'tbt_33_enable',
            template: `
sub tbt_33_enable(shutdown_io as pl_io_num)
    io.num=shutdown_io 
    io.state=HIGH
end sub
        `,
        });

        blocks.push({
            location: 'tbt33_system_hard_reboot',
            blockType: BlockType.CODE,
            id: `tbt33_system_hard_reboot_pin${this.getSettingValue('SOCKET')}`,
            template: `
    tbt_33_shut_down(TBT33_SHUTDOWN_S${this.getSettingValue('SOCKET')})
`,
        });

        return blocks;
    }


    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        if (Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id)?.interrupt === undefined
            && this.getSettingValue('PF') === '1') {
            errors.push({
                messageType: ValidationMessageType.UPDATE,
                message: '',
                featureId: this.getID(),
                settingId: 'PF',
                data: '0',
            });
        }
        switch (this.getSettingValue('SOCKET')) {
            case '17':
            case '19':
            case '21':
                // errors.push({
                //     message: `Power failure monitoring with interrupt
                // unavailable on socket ${this.getSettingValue('SOCKET')}`,
                //     messageType: ValidationMessageType.ERROR,
                // });
                break;
            case '23':
            case '25':
                // errors.push({
                //     message: `Power failure monitoring unavailable
                // on socket ${this.getSettingValue('SOCKET')}`,
                //     messageType: ValidationMessageType.ERROR,
                // });
                break;
            case '27':
                // errors.push({
                //     message: `Shut down functionality and power failure monitoring
                // unavailable on socket ${this.getSettingValue('SOCKET')}`,
                //     messageType: ValidationMessageType.ERROR,
                // });
                break;
            default:
        }
        return errors;
    }
}
